import * as React from 'react';
import {
  useQuery,
  useInfiniteQuery,
  useQueryClient,
  useMutation
} from 'react-query';

import {
  getCateringLocations,
  getCateringLocationFulfillmentHours,
  getCateringLocationDeliveryZoneFromAddress,
  getCateringLocationFirstAvailableFulfillmentHours,
  getCateringLocationMenuAvailability,
  getCateringLocationsWithUserAddress,
  getCateringLocationInventory
} from 'catering/api/resources/location';
import {
  selectCateringLocationById,
  selectCateringLocationBySlug,
  selectCateringLocationAvailableFulfillmentOptions,
  selectCateringLocationFulfillmentHours
} from 'catering/hooks/query/location/selectors';

export const LOCATIONS_QUERY_KEY = 'locations';
export const LOCATION_FULFILLMENT_HOURS_QUERY_KEY =
  'location-fulfillment-hours';
export const LOCATION_FIRST_AVAILABLE_FULFILLMENT_HOURS_QUERY_KEY =
  'location-first-available-fulfillment-hours';
export const LOCATION_DELIVERY_ZONE_QUERY_KEY = 'location-delivery-zone';
export const LOCATION_MENU_AVAILABILITY_QUERY_KEY = 'location-menus';
export const LOCATION_INVENTORY_QUERY_KEY = 'location-inventory';

const DEFAULT_QUERY_OPTIONS = {
  staleTime: 1000 * 60 * 60,
  cacheTime: 1000 * 60 * 60
};

function useCateringLocationsQuery(customQueryOptions = {}) {
  return useQuery(LOCATIONS_QUERY_KEY, getCateringLocations, {
    ...DEFAULT_QUERY_OPTIONS,
    ...customQueryOptions
  });
}

export function usePrefetchLocations() {
  const queryClient = useQueryClient();
  React.useEffect(() => {
    (async () => {
      await queryClient.prefetchQuery(
        LOCATIONS_QUERY_KEY,
        getCateringLocations
      );
    })();
  }, [queryClient]);
}

export function useCateringLocations(customQueryOptions) {
  return useCateringLocationsQuery(customQueryOptions);
}

export function useCateringLocationById(locationId, customQueryOptions) {
  return useCateringLocationsQuery({
    ...customQueryOptions,
    select: locations => selectCateringLocationById(locations, locationId)
  });
}

export function useCateringLocationBySlug(locationSlug, customQueryOptions) {
  return useCateringLocationsQuery({
    ...customQueryOptions,
    select: locations => {
      const location = selectCateringLocationBySlug(locations, locationSlug);
      return {
        ...location,
        availableFulfillmentOptions: selectCateringLocationAvailableFulfillmentOptions(
          location
        )
      };
    }
  });
}

export function useCateringLocationFulfillmentHours({
  locationSlug,
  fulfillmentMethod,
  startDate,
  customQueryOptions
}) {
  return useInfiniteQuery(
    [
      LOCATION_FULFILLMENT_HOURS_QUERY_KEY,
      {
        locationSlug,
        fulfillmentMethod,
        startDate
      }
    ],
    ({ pageParam = startDate }) =>
      getCateringLocationFulfillmentHours({
        locationSlug,
        queryParams: {
          fulfillmentMethod,
          startDate: pageParam
        }
      }),
    {
      ...DEFAULT_QUERY_OPTIONS,
      enabled: !!startDate && !!locationSlug && !!fulfillmentMethod,
      keepPreviousData: true,
      select: fulfillmentHours =>
        selectCateringLocationFulfillmentHours(fulfillmentHours),
      ...customQueryOptions
    }
  );
}

export function useCateringLocationFirstAvailableFulfillmentHours({
  locationSlug,
  customQueryOptions
}) {
  return useQuery(
    [LOCATION_FIRST_AVAILABLE_FULFILLMENT_HOURS_QUERY_KEY, locationSlug],
    () => getCateringLocationFirstAvailableFulfillmentHours(locationSlug),
    {
      ...DEFAULT_QUERY_OPTIONS,
      enabled: !!locationSlug,
      ...customQueryOptions
    }
  );
}
export function useCateringLocationDeliveryZoneFromAddress({
  locationSlug,
  queryParams,
  customQueryOptions
}) {
  return useQuery(
    [
      LOCATION_DELIVERY_ZONE_QUERY_KEY,
      {
        locationSlug,
        ...queryParams
      }
    ],
    () => getCateringLocationDeliveryZoneFromAddress(locationSlug, queryParams),
    {
      staleTime: 0,
      cacheTime: 0,
      useErrorBoundary: false,
      ...customQueryOptions
    }
  );
}
export function useCateringLocationMenuAvailability({
  locationSlug,
  customQueryOptions
}) {
  return useQuery(
    [LOCATION_MENU_AVAILABILITY_QUERY_KEY, locationSlug],
    () => getCateringLocationMenuAvailability(locationSlug),
    {
      ...DEFAULT_QUERY_OPTIONS,
      ...customQueryOptions,
      enabled: !!locationSlug
    }
  );
}

export function useUpdateCateringLocation() {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      shipping_city,
      shipping_country,
      shipping_google_place_id,
      shipping_postal_code,
      shipping_state,
      shipping_street_address_1,
      shipping_lat,
      shipping_lng
    }) => {
      return getCateringLocationsWithUserAddress({
        shipping_city,
        shipping_country,
        shipping_google_place_id,
        shipping_postal_code,
        shipping_state,
        shipping_street_address_1,
        shipping_lat,
        shipping_lng
      });
    },
    {
      onSuccess: updatedLocations => {
        const prevLocations = queryClient.getQueryData(LOCATIONS_QUERY_KEY);
        const locations = prevLocations.map(prevLocation => {
          const deliveryInfo = updatedLocations.find(
            updatedLocation => updatedLocation.id === prevLocation.id
          );
          return { ...prevLocation, ...deliveryInfo };
        });
        queryClient.setQueryData(LOCATIONS_QUERY_KEY, locations);
      }
    }
  );
}

export const useCateringLocationInventory = ({
  locationSlug,
  fulfillmentDateTime
}) =>
  useInfiniteQuery(
    [
      LOCATION_INVENTORY_QUERY_KEY,
      {
        locationSlug,
        fulfillmentDateTime
      }
    ],
    ({ pageParam = 1 }) =>
      getCateringLocationInventory(locationSlug, {
        fulfillmentDateTime,
        page: pageParam
      }),
    {
      ...DEFAULT_QUERY_OPTIONS,
      enabled: !!fulfillmentDateTime,
      keepPreviousData: true,
      getNextPageParam: lastPage => {
        const nextPage = lastPage?.next;
        if (!nextPage) {
          return null;
        }
        const url = new URL(nextPage);
        return url.searchParams.get('page');
      }
    }
  );

export * from 'catering/hooks/query/location/selectors';
